import { FileText } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { safeHref } from "@/lib/safeHref";

/**
 * Small, theme-tokenized chip linking to an open-access PDF (sourced from OpenAlex).
 * Reused in three places:
 *   - the My Library search result row (add-source dialog)
 *   - the My Library item card
 *   - the brief citations list in the composer
 *
 * Renders nothing when no URL is provided, so callers can use it unconditionally.
 */
type Props = {
  /** Open-access PDF URL. When null/undefined, nothing renders. */
  href: string | null | undefined;
  /** Visual size — `chip` for inline metadata rows, `inline` for sibling-of-text usage. */
  variant?: "chip" | "inline";
  className?: string;
};

const TOOLTIP = "Open-access PDF via OpenAlex";

export const OpenAccessLink = ({ href, variant = "chip", className }: Props) => {
  const safe = safeHref(href);
  if (!safe) return null;

  if (variant === "inline") {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={safe}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={TOOLTIP}
              className={
                "ml-2 inline-flex items-center gap-1 text-xs text-primary underline decoration-dotted underline-offset-2 hover:opacity-90 " +
                (className ?? "")
              }
              onClick={(e) => e.stopPropagation()}
            >
              <FileText className="h-3 w-3" aria-hidden="true" />
              Free PDF
            </a>
          </TooltipTrigger>
          <TooltipContent>{TOOLTIP}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={safe}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={TOOLTIP}
            className={
              "inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/15 " +
              (className ?? "")
            }
            onClick={(e) => e.stopPropagation()}
          >
            <FileText className="h-3 w-3" aria-hidden="true" />
            Free PDF
          </a>
        </TooltipTrigger>
        <TooltipContent>{TOOLTIP}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
